import React, { useMemo } from "react";
import {
  Page,
  Text,
  View,
  Document,
  pdf,
} from "@react-pdf/renderer";
import moment from "moment";
import config from "../../../config";
import PrintHeaderAccount from "../../../Pdf/PrintHeaderAccount";
import PrintFooter from "../../../Pdf/PrintFooter";
import { calculateColumnTotal } from "../../Shared/Utility";
import { saleReportStyles } from "../../../Pdf/pdfStyles";
import {
  PrintFormateNumber,
  parseReportAmount,
} from "../../../Pdf/PrintFormateNumber";

export const TABLE_WIDTH = 567;

export function buildLedgerColumns(flags) {
  const optionalExtraCount = [flags.product, flags.rate, flags.quantity].filter(
    Boolean
  ).length;

  const cols = {
    sr: 24,
    dated: 50,
    receipt: optionalExtraCount >= 2 ? 40 : 46,
    vehicle: optionalExtraCount >= 2 ? 52 : 62,
    debit: 52,
    credit: 52,
    balance: 52,
    type: 34,
  };

  if (flags.product) cols.product = 40;
  if (flags.rate) cols.rate = 40;
  if (flags.quantity) cols.quantity = 40;

  const fixedSum = Object.values(cols).reduce((sum, width) => sum + width, 0);

  if (flags.description) {
    cols.description = Math.max(60, TABLE_WIDTH - fixedSum);
  } else {
    cols.receipt += TABLE_WIDTH - fixedSum;
  }

  const total = Object.values(cols).reduce((sum, width) => sum + width, 0);
  if (total !== TABLE_WIDTH && flags.description) {
    cols.description += TABLE_WIDTH - total;
  } else if (total !== TABLE_WIDTH) {
    cols.receipt += TABLE_WIDTH - total;
  }

  return cols;
}

export function getColumnOrder(flags) {
  const order = ["sr", "dated"];
  if (flags.description) order.push("description");
  if (flags.product) order.push("product");
  if (flags.rate) order.push("rate");
  if (flags.quantity) order.push("quantity");
  order.push("receipt", "vehicle", "debit", "credit", "balance", "type");
  return order;
}

const COLUMN_LABELS = {
  sr: "Sr.#",
  dated: "Dated",
  description: "Description",
  product: "Product",
  rate: "Rate",
  quantity: "Qty",
  receipt: "Receipt",
  vehicle: "Vehicle",
  debit: "Debit",
  credit: "Credit",
  balance: "Balance",
  type: "Type",
};

const fmtMoney = (val) => PrintFormateNumber(parseReportAmount(val), 0);

function filterLedgerRows(ledgerRaw, printType) {
  if (printType === "Sale") {
    return (ledgerRaw || []).filter((item) => item.Reference_Type === "Sale");
  }
  if (printType === "Voucher") {
    return (ledgerRaw || []).filter((item) => item.Reference_Type === "Voucher");
  }
  return ledgerRaw || [];
}

function buildPeriodLabel(startDate, endDate) {
  if (startDate && endDate) {
    return `${moment(startDate).format(config.date_format)} to ${moment(endDate).format(config.date_format)}`;
  }
  if (startDate) {
    return `From ${moment(startDate).format(config.date_format)}`;
  }
  if (endDate) {
    return `Up to ${moment(endDate).format(config.date_format)}`;
  }
  return "All dates";
}

function buildReportTitle(printType, accountName) {
  const name = accountName || "Account";
  if (printType === "Sale") return `Ledger Sales — ${name}`;
  if (printType === "Voucher") return `Ledger Vouchers — ${name}`;
  return `Account Ledger — ${name}`;
}

export function AccountLedgerPdfDocument({
  accountName = "",
  ledgerRaw = [],
  printType = "All",
  columnFlags = {},
  startDate = null,
  endDate = null,
}) {
  const flags = useMemo(
    () => ({
      product: Boolean(columnFlags.product),
      rate: Boolean(columnFlags.rate),
      quantity: Boolean(columnFlags.quantity),
      description: columnFlags.description !== false,
    }),
    [columnFlags]
  );

  const cols = useMemo(() => buildLedgerColumns(flags), [flags]);
  const columnOrder = useMemo(() => getColumnOrder(flags), [flags]);
  const listofLedger = useMemo(
    () => filterLedgerRows(ledgerRaw, printType),
    [ledgerRaw, printType]
  );

  const firstEntry = ledgerRaw[0];
  const lastEntry = ledgerRaw[ledgerRaw.length - 1];
  const firstBalance = firstEntry ? parseReportAmount(firstEntry.Balance) : 0;
  const firstDate = firstEntry?.Transaction_Date;
  const lastBalance = lastEntry ? parseReportAmount(lastEntry.Balance) : 0;

  const periodLabel = buildPeriodLabel(startDate, endDate);
  const reportTitle = buildReportTitle(printType, accountName);

  const labelWidthBeforeAmounts = columnOrder
    .slice(0, columnOrder.indexOf("debit"))
    .reduce((sum, key) => sum + (cols[key] || 0), 0);

  const getCellValue = (item, key) => {
    switch (key) {
      case "dated":
        return item.Transaction_Date
          ? moment(item.Transaction_Date).format(config.date_format)
          : "—";
      case "description":
        return item.Description || "—";
      case "product":
        return item.Product || item.ProductName || "—";
      case "rate":
        return item.Rate != null ? fmtMoney(item.Rate) : "—";
      case "quantity":
        return item.Quantity != null
          ? PrintFormateNumber(item.Quantity)
          : "—";
      case "receipt":
        return item.Receipt_No || "—";
      case "vehicle":
        return item.Vehicle_No || "—";
      case "debit":
        return fmtMoney(item.Debit);
      case "credit":
        return fmtMoney(item.Credit);
      case "balance":
        return fmtMoney(item.Balance);
      case "type":
        return item.BalanceType || "";
      default:
        return "—";
    }
  };

  const isAmountColumn = (key) =>
    key === "debit" || key === "credit" || key === "balance";

  const renderHeader = () => (
    <View style={saleReportStyles.tableHead}>
      {columnOrder.map((key) => (
        <View
          key={key}
          style={[
            saleReportStyles.th,
            {
              width: cols[key],
              textAlign: isAmountColumn(key) ? "right" : "left",
            },
          ]}
        >
          <Text style={saleReportStyles.thValue}>{COLUMN_LABELS[key]}</Text>
        </View>
      ))}
    </View>
  );

  const renderRow = (item, index, options = {}) => {
    const {
      showBalance = printType !== "Sale",
      showType = printType !== "Sale",
      srOverride,
    } = options;

    return (
      <View key={item.ID || `row-${index}`} style={saleReportStyles.row}>
        {columnOrder.map((key) => {
          if (key === "sr") {
            return (
              <View key={key} style={[saleReportStyles.td, { width: cols.sr }]}>
                <Text style={saleReportStyles.tdValue}>
                  {srOverride ?? index + 1}
                </Text>
              </View>
            );
          }

          let value = getCellValue(item, key);
          if (key === "balance" && !showBalance) value = "";
          if (key === "type" && !showType) value = "";

          return (
            <View
              key={key}
              style={[
                saleReportStyles.td,
                {
                  width: cols[key],
                  textAlign: isAmountColumn(key) ? "right" : "left",
                },
              ]}
            >
              <Text style={saleReportStyles.tdValue}>{value}</Text>
            </View>
          );
        })}
      </View>
    );
  };

  const renderOpeningBalanceRow = () => {
    if (printType !== "Sale" || !firstDate) return null;

    return renderRow(
      {
        Receipt_No: "",
        Transaction_Date: firstDate,
        Vehicle_No: "",
        Debit: firstBalance,
        Credit: 0,
        Balance: "",
        BalanceType: "",
      },
      -1,
      { showBalance: false, showType: false, srOverride: 1 }
    );
  };

  const renderDataRows = () => {
    const startIndex = printType === "Sale" && firstDate ? 1 : 0;
    return listofLedger.map((item, index) =>
      renderRow(item, index + startIndex, {
        showBalance: printType !== "Sale",
        showType: printType !== "Sale",
      })
    );
  };

  const renderTotals = () => {
    if (printType === "All") {
      return (
        <View style={saleReportStyles.totalsRow}>
          <View
            style={[
              saleReportStyles.th,
              {
                width: labelWidthBeforeAmounts + cols.debit + cols.credit,
                textAlign: "right",
              },
            ]}
          >
            <Text style={saleReportStyles.thValue}>Total</Text>
          </View>
          <View
            style={[
              saleReportStyles.th,
              { width: cols.balance, textAlign: "right" },
            ]}
          >
            <Text style={saleReportStyles.thValue}>{fmtMoney(lastBalance)}</Text>
          </View>
          <View style={[saleReportStyles.th, { width: cols.type }]}>
            <Text style={saleReportStyles.thValue}> </Text>
          </View>
        </View>
      );
    }

    if (printType === "Sale") {
      const totalDebit =
        calculateColumnTotal(listofLedger, "Debit") + firstBalance;
      return (
        <View style={saleReportStyles.totalsRow}>
          <View
            style={[
              saleReportStyles.th,
              { width: labelWidthBeforeAmounts, textAlign: "right" },
            ]}
          >
            <Text style={saleReportStyles.thValue}>Total</Text>
          </View>
          <View
            style={[
              saleReportStyles.th,
              { width: cols.debit, textAlign: "right" },
            ]}
          >
            <Text style={saleReportStyles.thValue}>{fmtMoney(totalDebit)}</Text>
          </View>
          <View style={[saleReportStyles.th, { width: cols.credit }]}>
            <Text style={saleReportStyles.thValue}> </Text>
          </View>
          <View style={[saleReportStyles.th, { width: cols.balance }]}>
            <Text style={saleReportStyles.thValue}> </Text>
          </View>
          <View style={[saleReportStyles.th, { width: cols.type }]}>
            <Text style={saleReportStyles.thValue}> </Text>
          </View>
        </View>
      );
    }

    return null;
  };

  return (
    <Document>
      <Page size="A4" style={saleReportStyles.page}>
        <PrintHeaderAccount compact />
        <View style={saleReportStyles.section}>
          <View style={saleReportStyles.titleBlock}>
            <Text style={saleReportStyles.pagetitle}>{reportTitle}</Text>
            <Text style={saleReportStyles.reportSubtitle}>
              Period: {periodLabel}
            </Text>
            <Text style={saleReportStyles.reportSubtitle}>
              Entries: {listofLedger.length}
            </Text>
          </View>

          <View style={[saleReportStyles.table, { width: TABLE_WIDTH }]}>
            {renderHeader()}
            {renderOpeningBalanceRow()}
            {renderDataRows()}
            {renderTotals()}
          </View>
        </View>
        <PrintFooter fixed />
      </Page>
    </Document>
  );
}

export async function generateAccountLedgerPdfBase64(documentProps) {
  const blob = await pdf(
    <AccountLedgerPdfDocument {...documentProps} />
  ).toBlob();

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = String(reader.result || "");
      const commaIndex = result.indexOf(",");
      resolve(commaIndex >= 0 ? result.slice(commaIndex + 1) : result);
    };
    reader.onerror = () => reject(new Error("Failed to encode PDF"));
    reader.readAsDataURL(blob);
  });
}

export function buildLedgerPdfFileName(accountName) {
  const safeName = String(accountName || "Account")
    .replace(/[^\w\s-]/g, "")
    .trim()
    .replace(/\s+/g, "_");
  return `${safeName || "Account"}_Ledger_${moment().format("YYYY-MM-DD")}.pdf`;
}
